import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAdminAppointments, getAdminAppointment } from '../../api';
import {
  PageHeader, Table, Pagination, EmptyState, TableSkeleton, StatusBadge, UserCell, AstrologerCell,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatCurrency, formatDateTime } from '../../utils/format';
import { Calendar } from 'lucide-react';

const LIMIT = 20;
const STATUSES = ['', 'pending', 'confirmed', 'completed', 'cancelled', 'missed'];

export default function AppointmentsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-appointments', page, status],
    queryFn: () =>
      getAdminAppointments({ page, limit: LIMIT, status: status || undefined }).then((r) => r.data.data),
    placeholderData: (prev) => prev,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-appointment', detailId],
    queryFn: () => getAdminAppointment(detailId!).then((r) => r.data.data.appointment),
    enabled: !!detailId,
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'scheduledAt', label: 'Scheduled' },
    { key: 'amount', label: 'Amount (paise)' },
  ];

  const typeLabel = (t: string) => (t === 'call' ? 'Audio' : t);

  return (
    <div className="p-6">
      <PageHeader title="Appointments" subtitle="Scheduled consultations across chat, audio and video">
        <ExportButton data={rows} filename="appointments" columns={EXPORT_COLS} />
      </PageHeader>

      <OverviewBar
        items={[
          { label: 'Total', value: pagination?.total ?? '—', icon: <Calendar size={14} /> },
          { label: 'On page', value: rows.length },
        ]}
        loading={isLoading}
      />

      <div className="card p-4 mb-5 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s || 'all'}
            onClick={() => { setStatus(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium capitalize ${status === s ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600'}`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={8} cols={6} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No appointments found" icon={<Calendar size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">User</th>
                <th className="table-th">Astrologer</th>
                <th className="table-th">Type</th>
                <th className="table-th">Scheduled</th>
                <th className="table-th">Amount</th>
                <th className="table-th">Status</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a: any) => (
                <tr key={a._id} className="table-row">
                  <td className="table-td"><UserCell entity={a.userId} /></td>
                  <td className="table-td"><AstrologerCell entity={a.astrologerId} /></td>
                  <td className="table-td text-xs capitalize">{typeLabel(a.type)}</td>
                  <td className="table-td text-xs">{formatDateTime(a.scheduledAt)}</td>
                  <td className="table-td">{formatCurrency(a.amount || a.totalAmount || 0)}</td>
                  <td className="table-td"><StatusBadge status={a.status} /></td>
                  <td className="table-td">
                    <button className="btn-secondary btn-sm" onClick={() => setDetailId(a._id)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>

      <Modal open={!!detailId} onClose={() => setDetailId(null)} title="Appointment detail">
        {detailLoading || !detail ? (
          <TableSkeleton rows={4} cols={2} />
        ) : (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500">Status</dt><dd><StatusBadge status={detail.status} /></dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Type</dt><dd className="capitalize">{typeLabel(detail.type)}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Scheduled</dt><dd>{formatDateTime(detail.scheduledAt)}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Duration</dt><dd>{detail.durationMinutes || '—'} min</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Amount</dt><dd>{formatCurrency(detail.amount || detail.totalAmount || 0)}</dd></div>
          </dl>
        )}
      </Modal>
    </div>
  );
}
